import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity, AlertCircle, Cpu, Heart, Plug } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relTime, relTimeLong } from "@/lib/time";
import { useRealtime } from "@/lib/realtime";

export function System() {
  const healthQ = useQuery({
    queryKey: ["system", "health"],
    queryFn: () => api.systemHealth(),
    refetchInterval: 10_000,
  });

  // Build a 4-hour rolling sample of queue depth client-side from the
  // 10s polls. Cheap, doesn't need a backend timeseries endpoint.
  const samples = useQueueDepthHistory(healthQ.data?.queue_depth ?? 0);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold">System</h1>
          <p className="text-xs text-muted-foreground">
            Workers, queues, bridges. Polled every 10s.
          </p>
        </div>
      </header>

      <div className="grid gap-3 md:grid-cols-4">
        <Stat
          icon={<Cpu className="h-3.5 w-3.5" />}
          label="Workers"
          value={String(healthQ.data?.workers_running ?? "—")}
        />
        <Stat
          icon={<Activity className="h-3.5 w-3.5" />}
          label="Queue depth"
          value={String(healthQ.data?.queue_depth ?? "—")}
        />
        <Stat
          icon={<AlertCircle className="h-3.5 w-3.5" />}
          label="Errors / hr"
          value={String(healthQ.data?.errors_last_hour ?? "—")}
          tone={healthQ.data && healthQ.data.errors_last_hour > 0 ? "warn" : undefined}
        />
        <Stat
          icon={<Heart className="h-3.5 w-3.5" />}
          label="Last scheduler tick"
          value={relTime(healthQ.data?.scheduler_last_tick_at)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Queue depth — last 4h</CardTitle>
          <CardDescription>Client-side rolling sample.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-48 w-full">
            <ResponsiveContainer>
              <AreaChart data={samples}>
                <defs>
                  <linearGradient id="qDepth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--brand))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--brand))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" />
                <XAxis
                  dataKey="t"
                  fontSize={10}
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(t) => new Date(t).toLocaleTimeString().slice(0, 5)}
                />
                <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" />
                <RTooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    fontSize: 12,
                  }}
                  labelFormatter={(t) => new Date(t).toLocaleTimeString()}
                />
                <Area
                  type="monotone"
                  dataKey="depth"
                  stroke="hsl(var(--brand))"
                  fill="url(#qDepth)"
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending queue by job type</CardTitle>
          <CardDescription>
            Where the worker fleet is currently focused. Anything stuck for &gt;5min usually
            means a worker crash-looped — check the failed tab below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!healthQ.data ? (
            <Skeleton className="h-16 w-full" />
          ) : Object.keys(healthQ.data.queue_depth_by_type ?? {}).length === 0 ? (
            <Empty title="Queue is empty." />
          ) : (
            <div className="grid gap-2 md:grid-cols-3">
              {Object.entries(healthQ.data.queue_depth_by_type)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([jobType, n]) => {
                  const tone =
                    (n as number) > 50
                      ? "text-destructive"
                      : (n as number) > 10
                        ? "text-warning"
                        : "text-muted-foreground";
                  return (
                    <div
                      key={jobType}
                      className="flex items-baseline justify-between rounded-md border border-border/60 bg-card/40 px-3 py-2"
                    >
                      <span className="font-mono text-xs">{jobType}</span>
                      <span className={"tnum text-sm font-semibold " + tone}>{n as number}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bridges</CardTitle>
          <CardDescription>Mac-side BlueBubbles / WhatsApp / chat.db.</CardDescription>
        </CardHeader>
        <CardContent>
          {!healthQ.data ? (
            <Skeleton className="h-12 w-full" />
          ) : healthQ.data.bridges.length === 0 ? (
            <Empty title="No bridges connected." />
          ) : (
            <ul className="grid gap-2 md:grid-cols-2">
              {healthQ.data.bridges.map((b) => (
                <li
                  key={b.host_id}
                  className="flex items-center gap-3 rounded-md border border-border/60 bg-card/40 p-2 text-xs"
                >
                  <Plug
                    className={
                      b.status === "healthy"
                        ? "h-3.5 w-3.5 text-success"
                        : "h-3.5 w-3.5 text-destructive"
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono">{b.host_id}</div>
                    <div className="text-muted-foreground">
                      heartbeat {relTimeLong(b.last_heartbeat_at)}
                    </div>
                  </div>
                  <Badge tone={b.status === "healthy" ? "success" : "danger"}>
                    {b.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue">Queue inspector</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="pager">Pager log</TabsTrigger>
        </TabsList>
        <TabsContent value="queue">
          <QueueInspector />
        </TabsContent>
        <TabsContent value="events">
          <EventsTab />
        </TabsContent>
        <TabsContent value="pager">
          <PagerTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  tone?: "warn" | "danger";
}) {
  const cls =
    tone === "danger"
      ? "text-destructive"
      : tone === "warn"
        ? "text-warning"
        : "text-foreground";
  return (
    <Card>
      <CardContent className="flex items-center gap-3 px-4 py-3">
        {icon ? <div className="text-muted-foreground">{icon}</div> : null}
        <div className="min-w-0 flex-1">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={"tnum text-lg font-semibold " + cls}>{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function useQueueDepthHistory(current: number) {
  const [pts, setPts] = React.useState<{ t: number; depth: number }[]>([]);
  React.useEffect(() => {
    const now = Date.now();
    setPts((cur) => {
      const next = [...cur, { t: now, depth: current }];
      const cutoff = now - 4 * 3600_000;
      return next.filter((p) => p.t > cutoff);
    });
  }, [current]);
  return pts;
}

function QueueInspector() {
  const [status, setStatus] = React.useState("pending");
  const [jobType, setJobType] = React.useState("");
  const q = useQuery({
    queryKey: ["system", "queue", { status, jobType }],
    queryFn: () =>
      api.systemQueue({ status, job_type: jobType || undefined, limit: 200 }),
    refetchInterval: 5_000,
  });
  useRealtime("queue_jobs", () => q.refetch());

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["pending", "claimed", "complete", "failed"].map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="job_type filter…"
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          className="h-8 w-56 text-xs"
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card/40">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Enqueued</TableHead>
              <TableHead>Attempts</TableHead>
              <TableHead>Payload</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              <TableRow>
                <td colSpan={6} className="px-3 py-6">
                  <Skeleton className="h-5 w-full" />
                </td>
              </TableRow>
            ) : (q.data ?? []).length === 0 ? (
              <TableRow>
                <td colSpan={6} className="px-4 py-12">
                  <Empty title="No jobs match." />
                </td>
              </TableRow>
            ) : (
              (q.data ?? []).map((row) => (
                <TableRow key={row.job_id}>
                  <TableCell className="font-mono text-xs">{row.job_type}</TableCell>
                  <TableCell>
                    <Badge
                      tone={
                        row.status === "complete"
                          ? "success"
                          : row.status === "failed"
                            ? "danger"
                            : row.status === "claimed"
                              ? "info"
                              : "muted"
                      }
                    >
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="tnum text-muted-foreground">
                    {relTime(row.enqueued_at)}
                  </TableCell>
                  <TableCell className="tnum">{row.attempts}</TableCell>
                  <TableCell className="max-w-md truncate font-mono text-2xs text-muted-foreground">
                    {row.payload_summary}
                  </TableCell>
                  <TableCell className="max-w-md truncate text-2xs text-destructive">
                    {row.error ?? ""}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function EventsTab() {
  const [service, setService] = React.useState("");
  const q = useQuery({
    queryKey: ["system", "events", { service }],
    queryFn: () => api.systemEvents({ service: service || undefined, limit: 200 }),
    refetchInterval: 10_000,
  });
  useRealtime("events", () => q.refetch());

  return (
    <div className="space-y-3">
      <Input
        placeholder="service filter (eg bridge:imac01)"
        value={service}
        onChange={(e) => setService(e.target.value)}
        className="h-8 w-72 text-xs"
      />
      <div className="overflow-hidden rounded-lg border border-border bg-card/40">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Payload</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              <TableRow>
                <td colSpan={5} className="px-3 py-6">
                  <Skeleton className="h-5 w-full" />
                </td>
              </TableRow>
            ) : (q.data ?? []).length === 0 ? (
              <TableRow>
                <td colSpan={5} className="px-4 py-12">
                  <Empty title="No events." />
                </td>
              </TableRow>
            ) : (
              (q.data ?? []).map((row) => (
                <TableRow key={String(row.id)}>
                  <TableCell className="font-mono text-2xs text-muted-foreground">
                    {relTime(row.occurred_at)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.service}</TableCell>
                  <TableCell className="font-mono text-xs">{row.event_type}</TableCell>
                  <TableCell className="tnum text-xs text-muted-foreground">
                    {row.cost_cents != null ? (row.cost_cents / 100).toFixed(2) : "—"}
                  </TableCell>
                  <TableCell className="max-w-2xl truncate text-2xs text-muted-foreground">
                    {row.payload_summary}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PagerTab() {
  const q = useQuery({
    queryKey: ["pager_log"],
    queryFn: () => api.pagerLog({ limit: 100 }),
    refetchInterval: 30_000,
  });
  useRealtime("pager_log", () => q.refetch());

  return (
    <div className="space-y-2">
      {q.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (q.data ?? []).length === 0 ? (
        <Empty title="No pager activity." />
      ) : (
        (q.data ?? []).map((p) => (
          <Card key={p.id} className="p-3">
            <div className="flex items-center gap-2 text-xs">
              <Badge
                tone={p.severity === "urgent" ? "danger" : p.severity === "warn" ? "warning" : "muted"}
              >
                {p.severity}
              </Badge>
              <span className="font-mono text-muted-foreground">{p.service}</span>
              <span className="ml-auto text-muted-foreground">{relTimeLong(p.occurred_at)}</span>
            </div>
            <div className="mt-1 text-sm">{p.message}</div>
          </Card>
        ))
      )}
    </div>
  );
}
